import React, { useEffect } from 'react';
import { useProductDetailsPageContext } from '../ProductDetailsPage/ProductDetailsPageContext/ProductDetailsPageContext';

import ProductSizesForFixPreview from './ProductSizesForFixPreview';
import ProductSizesForKidsBusinessline from './ProductSizesForKidsBusinessline';
import ProductSizesForClientsWhoCannotShopAllSizes from './ProductSizesForClientsWhoCannotShopAllSizes';
import ProductSizesForProductsWithOneSize from './ProductSizesForProductsWithOneSize';
import ProductSizesForPersonalizedShoppingExperiences from './ProductSizesForPersonalizedShoppingExperience';
import ProductSizesForUnPersonalizedShoppingExperience from './ProductSizesForUnPersonalizedShoppingExperience';

const ProductSizes = () => {
  const [state, dispatch] = useProductDetailsPageContext();
  const currentSkuSize = state.onRecommendedSizesTab
    ? state.recommendedSkuSizeLabel || ''
    : state.selectedSizeLabel || '';
  const currentInseam = state.onRecommendedSizesTab
    ? state.recommendedSkuInseamLabel
    : state.selectedInseamLabel;

  const isOneSizeOnly = currentSkuSize.toLocaleLowerCase().includes('one size');

  // EXPL: JavaScript is better at detecting meaningful changes in strings
  // than arrays, so we use a "stringified" version of sizes and inseams
  // to avoid an infinite loop (state updates in useEffects below,
  // which triggers useEffect to run again, etc, etc)
  // The use of `useMemo` in state could circumvent the need for this,
  // but that is currently not an option with our state management system.

  // TOOD: revisit when Redux is introduced

  const sizeOptionsStrigified = state.sizeOptions
    .map(size => size.label)
    .join(',');
  const inseamsForSelectedSizeStringified =
    state.inseamOptions?.map(inseam => inseam?.label).join(',') || '';

  // EXPL: set default when selected option no longer valid
  useEffect(() => {
    const currentSizeOptionExists = !!state.sizeOptions.find(
      sizeOption => sizeOption.label === currentSkuSize,
    );

    if (!currentSizeOptionExists && !!state.sizeOptions[0]) {
      if (state.selectedSizeLabel) {
        dispatch({
          type: 'SIZE_SELECTED',
          payload: { sizeLabel: state.sizeOptions[0].label },
        });
      } else if (state.selectedColorLabel) {
        dispatch({
          type: 'SET_FALLBACK_SIZE',
          payload: { sizeLabel: state.sizeOptions[0].label },
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSkuSize, sizeOptionsStrigified]);

  useEffect(() => {
    const currentInseamOptionExists = !!state.inseamOptions.find(
      inseamOption => inseamOption?.label === currentInseam,
    );

    if (
      !!currentInseam &&
      !currentInseamOptionExists &&
      !!state.inseamOptions[0]
    ) {
      if (state.initialLoadSelectedState || state.clientSelectedInseam) {
        dispatch({
          type: 'INSEAM_SELECTED',
          payload: { inseamLabel: state.inseamOptions[0]?.label },
        });
      } else if (state.selectedColorLabel) {
        dispatch({
          type: 'SET_FALLBACK_INSEAM',
          payload: { inseamLabel: state.inseamOptions[0]?.label },
        });
      }
    } else if (!state.inseamOptions.length) {
      /*
       * Note: Some styles have variants which contain an inseam while others don't. An example is pants. Some regular pants may have inseam
       * while petite versions don't. This can cause a bug where we haven't cleared out inseam values in state and are not correctly loading data.
       * The check below ensures we are using the correct inseam values belonging to the variant.
       */

      if (state.selectedInseamLabel && state.clientSelectedColor) {
        // If we have a selected inseam label and client changes color, but don't have inseams for
        // a newly selected SKU, clear it out
        dispatch({
          type: 'INSEAM_SELECTED',
          payload: { inseamLabel: null },
        });
      }

      if (state.fallbackInseamLabel && state.clientSelectedColor) {
        // We may also need to clear out the fallback inseam value otherwise we could end up using the fallback
        // inseam values when there should be no inseam
        dispatch({
          type: 'SET_FALLBACK_INSEAM',
          payload: { inseamLabel: null },
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentInseam, inseamsForSelectedSizeStringified, state.selectedSkuId]);

  const showPersonalizedExperience =
    state.gated &&
    state.recommendedSkuId &&
    !state.clientBusinessLineIsMismatched &&
    (!state.eligibleForShoppingAcrossBusinessLines ||
      !state.hasNonPersonalizedFlag);

  if (state.isStaticView) {
    return <ProductSizesForFixPreview />;
  }

  if (isOneSizeOnly) {
    return <ProductSizesForProductsWithOneSize />;
  }

  if (state.productBusinessLine === 'KIDS') {
    return <ProductSizesForKidsBusinessline />;
  }

  if (state.gated && !state.shopAllSizes) {
    return <ProductSizesForClientsWhoCannotShopAllSizes />;
  }

  if (showPersonalizedExperience) {
    return <ProductSizesForPersonalizedShoppingExperiences />;
  }

  return <ProductSizesForUnPersonalizedShoppingExperience />;
};

export default ProductSizes;
